import type React from 'react'
import { forwardRef } from 'react'
import { useThemeColors } from '@/contexts/ThemeContext'
import useIsMobile from '@/hooks/useIsMobile'
import { theme } from '@/lib/theme'

interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  label?: string
  prefix?: string
  multiline?: boolean
  containerStyle?: React.CSSProperties
}

/**
 * Reusable input component with consistent styling and optional label/prefix support.
 * Supports both single-line and multiline (textarea) inputs.
 */
const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  (
    { label, prefix, multiline = false, containerStyle, style, id, ...props },
    ref
  ) => {
    const colors = useThemeColors()

    // NOTE: useIsMobile detects touch devices for touch-optimized input sizing and spacing
    const isMobile = useIsMobile()
    const inputStyles: React.CSSProperties = {
      width: '100%',
      padding: isMobile ? '10px 12px' : theme.spacing.sm,
      marginBottom: theme.spacing.lg,
      border: `1px solid ${colors.secondaryBorder}`,
      borderRadius: isMobile ? '10px' : theme.borderRadius.sm,
      backgroundColor: colors.secondaryBg,
      color: colors.primaryText,
      fontFamily: theme.fontFamily.inherit,
      boxSizing: 'border-box',
      fontSize: isMobile ? '12px' : 'inherit',
      ...style,
    }

    const labelStyles: React.CSSProperties = {
      display: 'block',
      marginBottom: theme.spacing.xs,
      fontSize: isMobile ? '12px' : theme.fontSize.sm,
      fontWeight: 'bold',
      color: colors.primaryText,
    }

    const prefixStyles: React.CSSProperties = {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-90%)',
      color: colors.lightText,
      paddingLeft: theme.spacing.md,
      pointerEvents: 'none',
    }

    const inputElement = multiline ? (
      <textarea
        ref={ref as React.Ref<HTMLTextAreaElement>}
        id={id}
        style={inputStyles}
        {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
      />
    ) : (
      <input
        ref={ref as React.Ref<HTMLInputElement>}
        type="text"
        id={id}
        style={{
          ...inputStyles,
          paddingLeft: prefix ? theme.spacing.xxl : theme.spacing.sm,
        }}
        {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
      />
    )

    return (
      <div style={containerStyle}>
        {label && (
          <label htmlFor={id} style={labelStyles}>
            {label}
          </label>
        )}
        <div style={{ position: 'relative' }}>
          {prefix && <span style={prefixStyles}>{prefix}</span>}
          {inputElement}
        </div>
      </div>
    )
  }
)

Input.displayName = 'Input'

export default Input
